/* ═══════════════════════════════════════════════════════════════
   HeroScanner.jsx
   Reusable building blocks for the OBSCURA hero section:
   - <ScanningDocument idx progress /> — the right-side animated doc
   - <FormatTicker idx dark /> — the cycling .pdf/.docx/... subline
   - HERO_FORMATS — list of supported formats
   - useScanCycle() — hook driving the auto-cycling animation
   ═══════════════════════════════════════════════════════════════ */

import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
  createContext,
} from "react";

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS — copia nel tuo CSS globale, oppure rimpiazza
   le var(--…) con i token del tuo design system:

   :root {
     --ink:        #061427;
     --f-display:  "Kumbh Sans", system-ui, sans-serif;
     --f-body:     "Roboto", system-ui, sans-serif;
     --ease:       cubic-bezier(.22, 1, .36, 1);
   }
   ────────────────────────────────────────────────────────────── */

export const HERO_FORMATS = [
  { ext: "pdf",  tone: "#dc2626" },
  { ext: "docx", tone: "#2563eb" },
  { ext: "csv",  tone: "#16a34a" },
  { ext: "pptx", tone: "#ea580c" },
  { ext: "png",  tone: "#7c3aed" },
  { ext: "jpg",  tone: "#0891b2" },
];

/* ── unified cycle: type → scan → pause → delete → next format ── */
export function useScanCycle({
  typeMs = 72,
  deleteMs = 40,
  scanMs = 3600,
  exitMs = 450,
  holdMs = 700,
} = {}) {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let idxLocal = 0;
    let raf = 0;
    const timers = [];

    const sleep = (ms) => new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timers.push(id);
    });

    const waitFrame = () => new Promise((resolve) => {
      raf = requestAnimationFrame(resolve);
    });

    const run = async () => {
      while (!cancelled) {
        const word = `.${HERO_FORMATS[idxLocal].ext}`;
        setIdx(idxLocal);
        setProgress(0);
        setDisplayed("");
        setTyping(true);

        for (let i = 1; i <= word.length; i += 1) {
          if (cancelled) return;
          setDisplayed(word.slice(0, i));
          await sleep(typeMs);
        }
        setTyping(false);

        const scanStart = performance.now();
        while (!cancelled) {
          const elapsed = performance.now() - scanStart;
          if (elapsed < scanMs) {
            setProgress(elapsed / scanMs);
          } else if (elapsed < scanMs + exitMs) {
            const t = (elapsed - scanMs) / exitMs;
            setProgress(1 + t * 0.15);
          } else if (elapsed < scanMs + exitMs + holdMs) {
            setProgress(1.15);
          } else {
            break;
          }
          await waitFrame();
        }

        setTyping(true);
        for (let i = word.length - 1; i >= 0; i -= 1) {
          if (cancelled) return;
          setDisplayed(word.slice(0, i));
          await sleep(deleteMs);
        }
        setTyping(false);

        idxLocal = (idxLocal + 1) % HERO_FORMATS.length;
      }
    };

    run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [typeMs, deleteMs, scanMs, exitMs, holdMs]);

  return { idx, progress, displayed, typing, format: HERO_FORMATS[idx] };
}

/* ── shared doc context (carries progress + container ref) ── */
const DocCtx = createContext({ progress: 0, containerRef: { current: null } });

/* ── <Red>: redactable token, auto-measures its own position
       so the black bar grows IN SYNC with the scanner line. ── */
function Red({ children, block = false, fill = "#0c1b35", pad = 0 }) {
  const { progress, containerRef } = useContext(DocCtx);
  const ref = useRef(null);
  const [b, setB] = useState({ left: 1, width: 0.01 });

  const measure = React.useCallback(() => {
    const el = ref.current;
    const cont = containerRef.current;
    if (!el || !cont) return;
    const er = el.getBoundingClientRect();
    const cr = cont.getBoundingClientRect();
    if (cr.width === 0 || er.width === 0) return;
    setB({
      left: (er.left - cr.left) / cr.width,
      width: Math.max(er.width / cr.width, 0.01),
      measured: true,
    });
  }, [containerRef]);

  useLayoutEffect(() => {
    let raf = 0;
    let timeout = 0;
    let ro;

    const measureSoon = () => {
      measure();

      if (containerRef.current) {
        if (!ro) {
          ro = new ResizeObserver(measure);
          ro.observe(containerRef.current);
          if (ref.current) ro.observe(ref.current);
        }
        return;
      }

      raf = requestAnimationFrame(measureSoon);
    };

    measureSoon();
    raf = requestAnimationFrame(measureSoon);
    timeout = setTimeout(measureSoon, 80); // re-measure after font load
    document.fonts?.ready?.then(measureSoon);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      ro?.disconnect();
    };
  }, [children, containerRef, measure]);

  useEffect(() => {
    if (progress <= 0 || progress >= 1) return undefined;
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [progress, measure]);

  const fillP = Math.max(0, Math.min(1, (progress - b.left + pad) / b.width));
  const Tag = block ? "div" : "span";

  return (
    <Tag
      ref={ref}
      style={{
        position: "relative",
        display: block ? "block" : "inline-block",
        whiteSpace: "nowrap",
        overflow: "visible",
      }}
    >
      <span style={{
        position: "relative", zIndex: 1,
      }}>{children}</span>
      <span aria-hidden style={{
        position: "absolute",
        left: -3,
        top: -2,
        bottom: -2,
        width: "calc(100% + 6px)",
        background: fill, borderRadius: 2,
        zIndex: 2,
        display: "block",
        pointerEvents: "none",
        transformOrigin: "left center",
        transform: `scaleX(${fillP})`,
      }}/>
    </Tag>
  );
}

/* ── <FaceBlur>: frosted wipe left→right in sync with scanner (same timing as <Red>) ── */
function FaceBlur({ children, style, blurFilterId, pad = 0.02 }) {
  const { progress, containerRef } = useContext(DocCtx);
  const ref = useRef(null);
  const [b, setB] = useState({ left: 0, width: 0.01 });

  const measure = React.useCallback(() => {
    const el = ref.current;
    const cont = containerRef.current;
    if (!el || !cont) return;
    const er = el.getBoundingClientRect();
    const cr = cont.getBoundingClientRect();
    if (cr.width === 0 || er.width === 0) return;
    setB({
      left: (er.left - cr.left) / cr.width,
      width: Math.max(er.width / cr.width, 0.01),
    });
  }, [containerRef]);

  useLayoutEffect(() => {
    let raf = 0;
    let timeout = 0;
    let ro;

    const measureSoon = () => {
      measure();
      if (containerRef.current) {
        if (!ro) {
          ro = new ResizeObserver(measure);
          ro.observe(containerRef.current);
          if (ref.current) ro.observe(ref.current);
        }
        return;
      }
      raf = requestAnimationFrame(measureSoon);
    };

    measureSoon();
    raf = requestAnimationFrame(measureSoon);
    timeout = setTimeout(measureSoon, 80);
    document.fonts?.ready?.then(measureSoon);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      ro?.disconnect();
    };
  }, [children, containerRef, measure]);

  useEffect(() => {
    if (progress <= 0 || progress >= 1) return undefined;
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [progress, measure]);

  const fillP = Math.max(0, Math.min(1, (progress - b.left + pad) / b.width));
  const { background, backgroundColor, ...boxStyle } = style || {};
  const bg = background || backgroundColor || "transparent";
  const blurFilter = blurFilterId ? `url(#${blurFilterId})` : "blur(10px)";
  const blurredChild = React.isValidElement(children)
    ? React.cloneElement(children, {
        style: { ...(children.props.style || {}), filter: blurFilter, WebkitFilter: blurFilter },
      })
    : children;

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        overflow: "hidden",
        ...boxStyle,
        background: bg,
      }}
    >
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        {children}
      </div>
      {fillP > 0 && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              transformOrigin: "left center",
              transform: `scaleX(${fillP})`,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: bg,
              }}
            >
              {blurredChild}
            </div>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255, 255, 255, 0.28)",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── scanner overlay (rendered inside DocFrame) ── */
function ScannerOverlay({ progress, color = "#009CDD" }) {
  return (
    <div aria-hidden style={{
      position: "absolute", inset: 0,
      pointerEvents: "none", overflow: "hidden",
      borderRadius: "inherit",
    }}>
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0,
        width: `${progress * 100}%`,
        background: `linear-gradient(90deg, transparent 0%, ${color}0d 30%, ${color}1f 100%)`,
      }}/>
      <div style={{
        position: "absolute", top: 0, bottom: 0,
        left: `calc(${progress * 100}% - 90px)`,
        width: 90,
        background: `linear-gradient(90deg, transparent, ${color}3a)`,
      }}/>
      <div style={{
        position: "absolute", top: 0, bottom: 0,
        left: `${progress * 100}%`,
        width: 2,
        background: `linear-gradient(180deg, ${color}66 0%, ${color} 10%, ${color} 90%, ${color}66 100%)`,
        boxShadow: `0 0 12px ${color}cc, 0 0 24px ${color}88`,
        opacity: progress > 0 && progress < 1.2 ? 1 : 0,
        transition: "opacity .25s ease-out",
      }}/>
      <div style={{
        position: "absolute", top: -4,
        left: `calc(${progress * 100}% - 6px)`,
        width: 12, height: 12, borderRadius: "50%",
        background: color,
        boxShadow: `0 0 10px ${color}`,
        opacity: progress > 0 && progress < 1.2 ? 1 : 0,
      }}/>
    </div>
  );
}

/* ── document frame (provides DocCtx + clips the scanner) ── */
function DocFrame({
  children, tone, label, filename,
  aspect = "portrait",
  progress = 0,
  scannerColor,
}) {
  const containerRef = useRef(null);
  const styleByAspect = aspect === "landscape"
    ? { width: "100%", aspectRatio: "16 / 10" }
    : aspect === "square"
    ? { width: "94%", aspectRatio: "1 / 1.18" }
    : { width: "92%", aspectRatio: "1 / 1.32" };
  return (
    <DocCtx.Provider value={{ progress, containerRef }}>
      <div ref={containerRef} style={{
        position: "relative",
        ...styleByAspect,
        background: "#fff",
        borderRadius: 8,
        boxShadow:
          "0 30px 60px -20px rgba(2,8,24,.55), 0 18px 30px -18px rgba(2,8,24,.4), inset 0 0 0 1px rgba(0,0,0,.04)",
        overflow: "hidden",
        color: "#0c1b35",
        fontFamily: "'Roboto', sans-serif",
      }}>
        <div style={{
          height: 26,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 12px",
          background: tone, color: "#fff",
          fontFamily: "'Roboto', sans-serif",
          fontSize: 10, letterSpacing: ".18em", fontWeight: 800,
        }}>
          <span>{label}</span>
          <span style={{ opacity: .85, fontWeight: 600 }}>{filename}</span>
        </div>
        {children}
        {scannerColor != null && <ScannerOverlay progress={progress} color={scannerColor}/>}
      </div>
    </DocCtx.Provider>
  );
}

/* ───────── FORMAT 1 · PDF (contratto) ───────── */
function DocPDF({ progress, scannerColor }) {
  return (
    <DocFrame tone="#dc2626" label="PDF" filename="contratto_2026_0421.pdf" progress={progress} scannerColor={scannerColor}>
      <div style={{ padding: "26px 28px", position: "relative", height: "calc(100% - 26px)" }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontFamily: "'Kumbh Sans', sans-serif", fontSize: 15, fontWeight: 800, letterSpacing: ".04em" }}>
            CONTRATTO DI FORNITURA
          </div>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9, color: "#64748b", letterSpacing: ".18em", marginTop: 4 }}>
            N. 0421 · 2026
          </div>
        </div>
        <div style={{ fontSize: 10.5, lineHeight: 1.7, color: "#334155" }}>
          <p style={{ marginBottom: 10 }}>
            Tra il Cliente <Red>Marco Rossi</Red>, P. IVA <Red>IT-12345678901</Red>, con sede in <Red>Via Garibaldi 42, Milano</Red>, e il Fornitore OBSCURA SRL si conviene quanto segue.
          </p>
          <p style={{ marginBottom: 10 }}>
            Il pagamento sarà effettuato sul conto <Red>IT60X0542811101000123456</Red> intestato al Cliente, secondo le scadenze concordate.
          </p>
          <p>
            Per qualsiasi comunicazione si farà riferimento all'indirizzo email <Red>m.rossi@acme.it</Red> e al recapito telefonico <Red>+39 348 555 1234</Red>.
          </p>
        </div>
        <div style={{
          position: "absolute", left: 28, right: 28, bottom: 22,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16,
        }}>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#94a3b8", letterSpacing: ".18em", marginBottom: 6 }}>FIRMA CLIENTE</div>
            <div style={{ borderTop: "1px solid #cbd5e1", paddingTop: 4, fontSize: 11, fontStyle: "italic" }}>
              <Red>Marco Rossi</Red>
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#94a3b8", letterSpacing: ".18em", marginBottom: 6 }}>DATA</div>
            <div style={{ borderTop: "1px solid #cbd5e1", paddingTop: 4, fontSize: 11 }}>14/03/2026</div>
          </div>
        </div>
      </div>
    </DocFrame>
  );
}

/* ───────── FORMAT 2 · DOCX (lettera) ───────── */
function DocDOCX({ progress, scannerColor }) {
  return (
    <DocFrame tone="#2563eb" label="DOCX" filename="lettera_cliente.docx" progress={progress} scannerColor={scannerColor}>
      <div style={{ padding: "28px 30px", height: "calc(100% - 26px)", position: "relative", fontFamily: "Cambria, Georgia, serif" }}>
        <div style={{ textAlign: "right", fontSize: 10, color: "#475569", marginBottom: 20 }}>
          Milano, 14 marzo 2026
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 16 }}>
          Spett.le<br/>
          <Red>Sig. Marco Rossi</Red><br/>
          <Red>Via Garibaldi 42</Red><br/>
          <Red>20121 Milano (MI)</Red>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
          Oggetto: Conferma fornitura anno 2026
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.7, color: "#334155", textAlign: "justify" }}>
          <p style={{ marginBottom: 10 }}>
            Gentile <Red>Sig. Rossi</Red>, con la presente confermiamo l'attivazione del contratto registrato presso la nostra sede in data 10 marzo 2026. Per eventuali comunicazioni potrà contattarci all'indirizzo <Red>amministrazione@acme.it</Red>.
          </p>
        </div>
        <div style={{ position: "absolute", left: 30, bottom: 24, fontSize: 11, fontStyle: "italic" }}>
          Cordiali saluti,<br/>
          <span style={{ fontStyle: "normal", fontWeight: 700 }}><Red>Dott.ssa Laura Bianchi</Red></span>
        </div>
      </div>
    </DocFrame>
  );
}

/* ───────── FORMAT 3 · CSV (tabella) ───────── */
function DocCSV({ progress, scannerColor }) {
  const cols = [
    { name: "NOME", w: "24%", redact: true },
    { name: "RUOLO", w: "22%", redact: false },
    { name: "IBAN", w: "34%", redact: true },
    { name: "STATUS", w: "20%", redact: false },
  ];
  const rows = [
    ["Marco Rossi", "Admin", "IT60X0542811101000123", "Attivo"],
    ["Laura Bianchi", "Finance", "IT12A0306909606100000", "Attivo"],
    ["Giulia Conti", "HR", "IT88B0103015900000010", "Review"],
    ["Luca Verdi", "Legal", "IT44C0301503200000048", "Attivo"],
    ["Anna Esposito", "Ops", "IT77D0306909606100100", "Review"],
    ["Paolo Greco", "Sales", "IT22E0306909606100200", "Attivo"],
  ];
  return (
    <DocFrame tone="#16a34a" label="CSV" filename="clienti_2026.csv" aspect="landscape" progress={progress} scannerColor={scannerColor}>
      <div style={{ height: "calc(100% - 26px)", padding: 0, position: "relative", fontFamily: "'Roboto', sans-serif" }}>
        <div style={{ display: "flex", background: "#f1f5f9", borderBottom: "1px solid #cbd5e1", height: 26, alignItems: "center" }}>
          {cols.map(c => (
            <div key={c.name} style={{
              width: c.w, padding: "0 8px",
              fontSize: 9, fontWeight: 800, letterSpacing: ".14em", color: "#334155",
              borderRight: "1px solid #cbd5e1",
            }}>{c.name}</div>
          ))}
        </div>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: "flex", height: 28, alignItems: "center",
            background: i % 2 ? "#fafbfc" : "#fff",
            borderBottom: "1px solid #e2e8f0",
          }}>
            {cols.map((c, ci) => (
              <div key={c.name} style={{
                width: c.w, padding: "0 8px",
                borderRight: "1px solid #e2e8f0",
                fontSize: 9.5, color: "#334155",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {c.redact ? <Red>{r[ci]}</Red> : r[ci]}
              </div>
            ))}
          </div>
        ))}
      </div>
    </DocFrame>
  );
}

/* ───────── FORMAT 4 · PPTX (slide) ───────── */
function DocPPTX({ progress, scannerColor }) {
  return (
    <DocFrame tone="#ea580c" label="PPTX" filename="q4_2025_review.pptx" aspect="landscape" progress={progress} scannerColor={scannerColor}>
      <div style={{
        height: "calc(100% - 26px)", position: "relative",
        background: "linear-gradient(180deg, #fff 0%, #fff7ed 100%)",
        padding: "20px 24px",
      }}>
        <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9, color: "#9a3412", letterSpacing: ".18em", fontWeight: 800 }}>
          OBSCURA · Q4 2025 REVIEW
        </div>
        <div style={{ fontFamily: "'Kumbh Sans', sans-serif", fontSize: 18, fontWeight: 700, color: "#0c1b35", marginTop: 6, letterSpacing: "-.01em", lineHeight: 1.15 }}>
          Top clienti: <Red>ACME SpA</Red> · <Red>Banca Lupi</Red>
        </div>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6, fontSize: 10, color: "#334155" }}>
            <li>▸ Ricavi: € 2,4M (+ 38% YoY)</li>
            <li>▸ Account owner: <Red>Marco Rossi</Red></li>
            <li>▸ Referente: <Red>marco@acme.it</Red></li>
            <li>▸ Sede: <Red>Via Garibaldi 42, MI</Red></li>
          </ul>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 80, padding: "0 4px" }}>
            {[40, 55, 48, 70, 62, 88, 76, 95].map((h, i) => (
              <div key={i} style={{
                flex: 1, height: `${h}%`,
                background: "linear-gradient(180deg, #fb923c, #ea580c)",
                borderRadius: "3px 3px 0 0",
              }}/>
            ))}
          </div>
        </div>
        <div style={{
          position: "absolute", left: 24, right: 24, bottom: 12,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontFamily: "'Roboto', sans-serif", fontSize: 9, color: "#94a3b8", letterSpacing: ".12em",
        }}>
          <span>SLIDE 4 / 12</span>
          <span>Autore: <Red>Luca Verdi</Red></span>
        </div>
      </div>
    </DocFrame>
  );
}

/* ───────── FORMAT 5 · PNG (carta d'identità) ───────── */
function DocPNG({ progress, scannerColor }) {
  return (
    <DocFrame tone="#7c3aed" label="PNG" filename="ci_scan.png" aspect="square" progress={progress} scannerColor={scannerColor}>
      <div style={{
        height: "calc(100% - 26px)", position: "relative",
        background: "linear-gradient(180deg, #faf5ff 0%, #fff 60%)",
        padding: 22,
      }}>
        <div style={{
          border: "1px solid #ddd6fe", borderRadius: 10,
          padding: 16, height: "100%",
          background: "#fff",
          boxShadow: "inset 0 0 0 1px rgba(124,58,237,.08)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".22em", color: "#7c3aed", fontWeight: 800 }}>
              REPUBBLICA ITALIANA
            </span>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#94a3b8", letterSpacing: ".14em" }}>
              CARTA D'IDENTITÀ
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 14 }}>
            <FaceBlur
              blurFilterId="ob-ci-face-blur"
              style={{
                width: 70, height: 88, borderRadius: 4,
                background: "linear-gradient(135deg, #e9d5ff, #c4b5fd)",
                border: "1px solid #ddd6fe",
              }}
            >
              <svg viewBox="0 0 70 88" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <filter id="ob-ci-face-blur" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="4.2"/>
                  </filter>
                  <linearGradient id="ob-ci-face" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ddd6fe"/>
                    <stop offset="55%" stopColor="#a78bfa"/>
                    <stop offset="100%" stopColor="#8b5cf6"/>
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="70" height="88" fill="url(#ob-ci-face)" opacity="0.55"/>
                <circle cx="35" cy="34" r="14" fill="#c4b5fd" opacity=".85"/>
                <ellipse cx="35" cy="74" rx="22" ry="14" fill="#c4b5fd" opacity=".85"/>
              </svg>
            </FaceBlur>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, fontSize: 10 }}>
              <IdField label="COGNOME">ROSSI</IdField>
              <IdField label="NOME">MARCO</IdField>
              <IdField label="NATO IL">15/08/1985 a MILANO</IdField>
              <IdField label="CODICE FISCALE">RSSMRC85M15F205X</IdField>
            </div>
          </div>
          <div style={{
            marginTop: 14, paddingTop: 10,
            borderTop: "1px dashed #ddd6fe",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
          }}>
            <IdField label="RESIDENZA">Via Garibaldi 42, Milano</IdField>
            <IdField label="SCADENZA">15/08/2030</IdField>
          </div>
          <div style={{
            marginTop: 12,
            fontFamily: "'Roboto', sans-serif", fontSize: 8.5, lineHeight: 1.3,
            color: "#1e293b", letterSpacing: ".05em",
            background: "#f8fafc", padding: "6px 8px", borderRadius: 4,
            border: "1px solid #e2e8f0",
            wordBreak: "break-all",
          }}>
            <Red block>IDITA RSSMRC85M15F205X&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</Red>
          </div>
        </div>
      </div>
    </DocFrame>
  );
}
function IdField({ label, children }) {
  return (
    <div>
      <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 7.5, letterSpacing: ".18em", color: "#94a3b8", fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 700, color: "#0c1b35" }}>
        <Red>{children}</Red>
      </div>
    </div>
  );
}

/* ───────── FORMAT 6 · JPG (fattura) ───────── */
function DocJPG({ progress, scannerColor }) {
  return (
    <DocFrame tone="#0891b2" label="JPG" filename="fattura_0421.jpg" progress={progress} scannerColor={scannerColor}>
      <div style={{ height: "calc(100% - 26px)", padding: "22px 26px", position: "relative", background: "#fffefb" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #0c1b35", paddingBottom: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: "'Kumbh Sans', sans-serif", fontSize: 18, fontWeight: 800, letterSpacing: "-.01em" }}>FATTURA</div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9.5, color: "#64748b", letterSpacing: ".14em", marginTop: 2 }}>N. 2026/0421</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 10, color: "#475569" }}>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".18em", color: "#94a3b8" }}>EMESSA IL</div>
            <div>14 / 03 / 2026</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 10, marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".18em", color: "#0891b2", fontWeight: 800, marginBottom: 4 }}>DA</div>
            <div style={{ fontWeight: 700, color: "#0c1b35" }}>OBSCURA SRL</div>
            <div style={{ color: "#475569" }}>Via Roma 1, 00100 Roma</div>
            <div style={{ color: "#475569" }}>P. IVA IT-09876543210</div>
          </div>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".18em", color: "#0891b2", fontWeight: 800, marginBottom: 4 }}>A</div>
            <div style={{ fontWeight: 700, color: "#0c1b35" }}><Red>Marco Rossi</Red></div>
            <div style={{ color: "#475569" }}><Red>Via Garibaldi 42, Milano</Red></div>
            <div style={{ color: "#475569" }}>P. IVA <Red>IT-12345678901</Red></div>
          </div>
        </div>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden", fontSize: 10 }}>
          <div style={{ display: "flex", background: "#0c1b35", color: "#fff", padding: "6px 10px", fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".14em", fontWeight: 800 }}>
            <span style={{ flex: 1 }}>DESCRIZIONE</span>
            <span style={{ width: 50, textAlign: "right" }}>Q.TÀ</span>
            <span style={{ width: 70, textAlign: "right" }}>IMPORTO</span>
          </div>
          {[
            ["Servizio anonimizzazione documenti", "1", "€ 1.200,00"],
            ["Setup on-premise + integrazione",    "1", "€ 800,00"],
          ].map(([d, q, p], i) => (
            <div key={i} style={{ display: "flex", padding: "6px 10px", borderTop: i === 0 ? "none" : "1px solid #f1f5f9", color: "#334155" }}>
              <span style={{ flex: 1 }}>{d}</span>
              <span style={{ width: 50, textAlign: "right" }}>{q}</span>
              <span style={{ width: 70, textAlign: "right", fontFamily: "'Roboto', sans-serif" }}>{p}</span>
            </div>
          ))}
        </div>
        <div style={{
          position: "absolute", left: 26, right: 26, bottom: 16,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 10, color: "#475569",
          paddingTop: 10, borderTop: "1px dashed #e2e8f0",
        }}>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".18em", color: "#94a3b8" }}>PAGAMENTO SU IBAN</div>
            <div style={{ fontFamily: "'Roboto', sans-serif", color: "#0c1b35" }}>
              <Red>IT60X0542811101000123456</Red>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, letterSpacing: ".18em", color: "#94a3b8" }}>TOTALE</div>
            <div style={{ fontFamily: "'Kumbh Sans', sans-serif", fontSize: 16, fontWeight: 800, color: "#0c1b35" }}>€ 2.000,00</div>
          </div>
        </div>
      </div>
    </DocFrame>
  );
}

const HERO_DOC_BASE_WIDTH = 460;
const HERO_LANDSCAPE_IDX = new Set([2, 3]); // csv, pptx

function getHeroDocHeight(idx) {
  if (idx === 2 || idx === 3) return HERO_DOC_BASE_WIDTH * (10 / 16);
  if (idx === 4) return HERO_DOC_BASE_WIDTH * 0.94 * 1.18;
  return HERO_DOC_BASE_WIDTH * 0.92 * 1.32;
}

const HERO_MAX_DOC_HEIGHT = Math.max(
  ...HERO_FORMATS.map((_, i) => getHeroDocHeight(i)),
);

function getHeroDocSlotHeight(isMobile, isTablet) {
  if (isMobile) return Math.min(320, HERO_MAX_DOC_HEIGHT * 0.78);
  if (isTablet) return Math.min(440, HERO_MAX_DOC_HEIGHT * 0.9);
  return Math.min(520, HERO_MAX_DOC_HEIGHT);
}

/* ───────── ScanningDocument — composes the right-side doc ───────── */
export function ScanningDocument({ idx, progress, isMobile = false, isTablet = false }) {
  const fmt = HERO_FORMATS[idx];
  const Doc = [DocPDF, DocDOCX, DocCSV, DocPPTX, DocPNG, DocJPG][idx];
  const wrapRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const desiredScale = isMobile ? 0.78 : isTablet ? 0.9 : 1;
  const docSlotHeight = getHeroDocSlotHeight(isMobile, isTablet);
  const docNaturalHeight = getHeroDocHeight(idx);
  const widthScale = availableWidth > 0 ? availableWidth / HERO_DOC_BASE_WIDTH : desiredScale;
  const heightScale = docSlotHeight / docNaturalHeight;
  const scale = Math.min(desiredScale, widthScale, heightScale);
  const isConstrained = scale < desiredScale - 0.001;
  const scaledDocWidth = HERO_DOC_BASE_WIDTH * scale;
  const scaledDocHeight = docNaturalHeight * scale;
  const isLandscape = HERO_LANDSCAPE_IDX.has(idx);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const measure = () => setAvailableWidth(el.getBoundingClientRect().width);
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const alignRight = !isMobile && !isTablet && !isConstrained;

  return (
    <div ref={wrapRef} style={{
      width: "100%",
      display: "flex",
      justifyContent: alignRight ? "flex-end" : "center",
    }}>
      <div style={{
        position: "relative",
        width: alignRight ? scaledDocWidth : "100%",
        maxWidth: scaledDocWidth,
        height: docSlotHeight,
        flexShrink: 0,
      }}>
        <div style={{
          height: docSlotHeight,
          display: "flex",
          justifyContent: "center",
          alignItems: isLandscape ? "center" : "flex-start",
        }}>
          <div style={{
            width: scaledDocWidth,
            height: scaledDocHeight,
            flexShrink: 0,
            overflow: "hidden",
          }}>
            <div style={{
              width: HERO_DOC_BASE_WIDTH,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}>
              <Doc key={idx} progress={progress} scannerColor={fmt.tone} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── character-by-character typewriter synced to scan idx ── */
function TypewriterCaret({ blink = true }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 2,
        height: "0.92em",
        marginLeft: 2,
        background: "currentColor",
        verticalAlign: "-0.08em",
        animation: blink ? "ob-hero-caret 0.85s steps(1) infinite" : "none",
      }}
    />
  );
}

/* ───────── FormatTicker — subline with typewriter .pdf/.docx/... ───────── */
export function FormatTicker({ idx, displayed = "", typing = true, dark = true }) {
  const fmt = HERO_FORMATS[idx];

  return (
    <>
      <style>{`
        @keyframes ob-hero-caret {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>
      <div style={{
        display: "flex", alignItems: "center", gap: 14,
        fontFamily: "'Kumbh Sans', sans-serif",
        fontSize: 22, fontWeight: 500,
        color: dark ? "rgba(255,255,255,.92)" : "#061427",
        lineHeight: 1.3, flexWrap: "wrap",
      }}>
        <span>Con OBSCURA puoi anonimizzare i tuoi</span>
        <span style={{
          position: "relative",
          display: "inline-flex", alignItems: "center",
          padding: "6px 14px",
          borderRadius: 10,
          background: `${fmt.tone}1f`,
          border: `1px solid ${fmt.tone}66`,
          fontFamily: "'Roboto', sans-serif",
          fontSize: 18, fontWeight: 800,
          color: dark ? "#fff" : "#061427",
          boxShadow: `0 0 24px ${fmt.tone}3a`,
          minWidth: 96,
        }}>
          <span style={{ whiteSpace: "nowrap" }}>
            {displayed}
            <TypewriterCaret blink={typing || displayed.length > 0} />
          </span>
        </span>
      </div>
    </>
  );
}
